/**
 * Pure enrollment/batch eligibility rules (SRP: business rules without I/O).
 * Easy to unit test; UI and hooks depend on these instead of inlining filters.
 */

export function getEnrollmentBatchId(enrollment) {
  if (!enrollment) return NaN;
  const id = enrollment.batch?.id ?? enrollment.batchId;
  const n = Number(id);
  return Number.isNaN(n) ? NaN : n;
}

/** Batch IDs the student already has an enrollment row for. */
export function getOccupiedBatchIds(enrollments) {
  const ids = new Set();
  for (const e of enrollments || []) {
    const bid = getEnrollmentBatchId(e);
    if (!Number.isNaN(bid)) ids.add(bid);
  }
  return ids;
}

/**
 * Batches available for a *new additional* enrollment (exclude all occupied).
 */
export function filterBatchesForAdditionalEnrollment(allBatches, enrollments) {
  if (!allBatches?.length) return [];
  const taken = getOccupiedBatchIds(enrollments);
  return allBatches.filter((b) => !taken.has(Number(b.id)));
}

/**
 * Batches valid as a transfer target: not the source batch, and not already
 * occupied by another enrollment row for this student.
 */
export function filterBatchesForBatchChange(allBatches, enrollments, fromEnrollmentId) {
  if (!allBatches?.length || !fromEnrollmentId) return [];
  const from = (enrollments || []).find((e) => String(e.id) === String(fromEnrollmentId));
  const fromBatchId = getEnrollmentBatchId(from);
  if (Number.isNaN(fromBatchId)) return [];

  return allBatches.filter((b) => {
    const bid = Number(b.id);
    if (bid === fromBatchId) return false;
    const hasOtherEnrollment = (enrollments || []).some(
      (e) =>
        String(e.id) !== String(fromEnrollmentId) && getEnrollmentBatchId(e) === bid
    );
    return !hasOtherEnrollment;
  });
}

/** Prefer ongoing enrollment, else most recently listed (caller controls order). */
export function pickDefaultSourceEnrollmentId(enrollments) {
  if (!enrollments?.length) return '';
  const ongoing = enrollments.find((e) => e.batchStatus === 'Ongoing');
  return String((ongoing || enrollments[0]).id);
}
